<template>
  <div
    ref="modalEl"
    class="modal fade flyover-share"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable flyover-share__dialog">
      <div class="modal-content flyover-share__content">
        <!-- Fixed top bar: close + (prompt/format only) edit -->
        <div class="flyover-share__topbar">
          <button type="button" class="flyover-share__close" @click="hide" :aria-label="$t('shared.close')">
            <span class="material-icons">close</span>
          </button>
        </div>

        <!-- STEP: checking for an existing render (avoids flashing the prompt/format step before we
             know a ready video can be reused) -->
        <div v-if="step === 'checking'" class="flyover-share__body">
          <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">{{ $t('shared.loading_2') }}</span>
          </div>
        </div>

        <!-- STEP: prompt -->
        <div v-else-if="step === 'prompt'" class="flyover-share__body">
          <img :src="pinMarker" class="flyover-share__pin-img" alt="" />
          <h2 class="flyover-share__title">{{ copy.promptTitle }}</h2>
          <div v-if="config.thumbnailUrl" class="flyover-share__map">
            <img :src="config.thumbnailUrl" :alt="$t('flyover_share.route_preview')" />
          </div>
          <div class="flyover-share__stats">
            <div class="flyover-share__stat"><span>{{ config.distance || '—' }}</span><label>{{ $t('shared.distance') }}</label></div>
            <div class="flyover-share__stat"><span>{{ config.elevation || '—' }}</span><label>{{ $t('shared.elevation') }}</label></div>
            <div class="flyover-share__stat"><span>{{ config.duration || '—' }}</span><label>{{ $t('shared.duration') }}</label></div>
          </div>
          <div class="flyover-share__actions">
            <button class="flyover-share__btn flyover-share__btn--primary" @click="step = 'chooseFormat'">{{ $t('shared.share') }}</button>
            <button class="flyover-share__btn flyover-share__btn--ghost" @click="hide">{{ $t('flyover_share.not_now') }}</button>
          </div>
        </div>

        <!-- STEP: choose format -->
        <div v-else-if="step === 'chooseFormat'" class="flyover-share__body">
          <img :src="pinMarker" class="flyover-share__pin-img" alt="" />
          <h2 class="flyover-share__title">{{ $t('flyover_share.choose_a_format') }}</h2>
          <p class="flyover-share__subtitle">{{ $t('flyover_share.pick_the_shape_that_fits_where') }}</p>
          <div class="flyover-share__formats">
            <button
              :class="['flyover-share__format', { 'is-selected': format === 'vertical' }]"
              @click="format = 'vertical'"
            >
              <div class="flyover-share__phone flyover-share__phone--vertical"><span class="flyover-share__notch"></span></div>
              <span>{{ $t('flyover_share.vertical') }}</span><small>{{ $t('flyover_share.9_16_stories_reels') }}</small>
            </button>
            <button
              :class="['flyover-share__format', { 'is-selected': format === 'horizontal' }]"
              @click="format = 'horizontal'"
            >
              <div class="flyover-share__phone flyover-share__phone--horizontal"><span class="flyover-share__notch"></span></div>
              <span>{{ $t('flyover_share.horizontal') }}</span><small>{{ $t('flyover_share.16_9_feed_youtube') }}</small>
            </button>
          </div>
          <!-- PRO+ video options. Collapsed to one row by default: the member who just wants to post
               the video should not have to read past it, and the row still states what it holds. A
               free account gets the same rows, always visible, each opening the paywall. -->
          <div class="flyover-share__options">
            <button
              v-if="!optionsExpanded"
              class="flyover-share__options-row"
              type="button"
              @click="expandOptions"
            >
              <span class="flyover-share__options-icon">
                <svg width="19" height="17" viewBox="0 0 46.506 40.878" fill="none" aria-hidden="true">
                  <path :d="hiikerMarkPath" fill="currentColor" fill-rule="nonzero" />
                </svg>
              </span>
              <span class="flyover-share__options-text">
                <span class="flyover-share__options-title">
                  {{ $t('flyover_share.video_options') }}
                  <img :src="proPlusBadge" class="flyover-share__pro-badge" :alt="$t('shared.pro')" />
                </span>
                <small>{{ $t('flyover_share.logo_stats_panel_intro_amp_outro') }}</small>
              </span>
              <span class="flyover-share__options-state">{{ collapsedOptionsLabel }}</span>
              <span class="flyover-share__chevron" aria-hidden="true"></span>
            </button>

            <template v-else>
              <div class="flyover-share__options-head">
                <span class="flyover-share__options-heading">{{ $t('flyover_share.video_options_2') }}</span>
                <img :src="proPlusBadge" class="flyover-share__pro-badge" :alt="$t('shared.pro')" />
                <button
                  v-if="isProMember"
                  class="flyover-share__options-collapse"
                  type="button"
                  @click="optionsExpanded = false"
                >{{ $t('flyover_share.hide') }}</button>
              </div>

              <div class="flyover-share__options-panel">
                <button
                  v-for="row in videoOptionRows"
                  :key="row.key"
                  class="flyover-share__option"
                  type="button"
                  :aria-pressed="isProMember ? videoOptions[row.key] : undefined"
                  @click="onOptionRow(row.key)"
                >
                  <span class="flyover-share__option-text">
                    <span>{{ row.label }}</span>
                    <small>{{ row.sublabel }}</small>
                  </span>
                  <!-- Labels stay at full contrast for a free account: the rows are an offer, not a
                       disabled control, so dimming them would read as "unavailable" rather than
                       "included with PRO+". -->
                  <span v-if="!isProMember" class="flyover-share__option-lock" aria-hidden="true">
                    <svg width="11" height="13" viewBox="0 0 11 13" fill="none">
                      <rect x="1" y="5.5" width="9" height="7" rx="1.8" fill="#2cc2e3" />
                      <path d="M3.2 5.5V4a2.3 2.3 0 014.6 0v1.5" stroke="#2cc2e3" stroke-width="1.4" stroke-linecap="round" />
                    </svg>
                  </span>
                  <span
                    v-else
                    class="flyover-share__toggle"
                    :class="{ 'is-on': videoOptions[row.key] }"
                    aria-hidden="true"
                  ><span class="flyover-share__toggle-knob"></span></span>
                </button>
              </div>

              <p class="flyover-share__options-summary">{{ optionsSummaryText }}</p>
            </template>
          </div>

          <div class="flyover-share__actions">
            <button class="flyover-share__btn flyover-share__btn--primary" @click="startGeneration">{{ $t('shared.continue') }}</button>
          </div>

          <!-- PRO+ paywall, over the format step rather than a route away: the member came here to
               share, and sending them to /purchases has to be their choice. -->
          <div v-if="paywallOpen" class="flyover-share__paywall" @click.self="paywallOpen = false">
            <div class="flyover-share__sheet">
              <span class="flyover-share__grab" aria-hidden="true"></span>
              <img :src="proPlusLogo" class="flyover-share__sheet-logo" :alt="$t('shared.hiiker_pro')" />
              <h3>{{ $t('flyover_share.export_a_bare_bones_video') }}</h3>
              <p>
                {{ copy.paywallBody }}
              </p>
              <ul class="flyover-share__sheet-bullets">
                <li>{{ $t('flyover_share.hide_the_hiiker_logo') }}</li>
                <li>{{ $t('flyover_share.hide_the_stats_panel') }}</li>
                <li>{{ $t('flyover_share.skip_the_intro_and_outro') }}</li>
              </ul>
              <button class="flyover-share__btn flyover-share__btn--pro" @click="upgradeToPro">{{ $t('flyover_share.upgrade_to_pro') }}</button>
              <button class="flyover-share__sheet-dismiss" @click="paywallOpen = false">{{ $t('flyover_share.not_now') }}</button>
            </div>
          </div>
        </div>

        <!-- STEP: generating -->
        <div v-else-if="step === 'generating'" class="flyover-share__body">
          <div class="flyover-share__ring" :style="ringStyle">
            <div class="flyover-share__ring-inner">
              <div ref="lottieContainer" class="flyover-share__lottie"></div>
            </div>
          </div>
          <h2 class="flyover-share__title">{{ $t('flyover_share.creating_your_video') }}</h2>
          <p class="flyover-share__subtitle">{{ displayProgress }}%</p>
          <div class="flyover-share__tip">
            <span class="material-icons">lightbulb</span>
            <div><strong>{{ tips[tipIndex].t }}</strong><p>{{ tips[tipIndex].b }}</p></div>
          </div>
        </div>

        <!-- STEP: ready -->
        <div v-else-if="step === 'ready'" class="flyover-share__ready">
          <div class="flyover-share__ready-top">
            <h2 class="flyover-share__title">{{ $t('flyover_share.your_video_is_ready_to_share') }}</h2>
            <div :class="['flyover-share__video', videoIsVertical ? 'is-vertical' : 'is-horizontal']">
              <video
                v-if="videoUrl"
                :src="videoUrl"
                autoplay
                loop
                muted
                playsinline
              ></video>
            </div>
          </div>
          <div class="flyover-share__sheet">
            <div class="flyover-share__sheet-row">
              <button
                class="flyover-share__pill"
                :class="{ 'is-busy': saving }"
                :disabled="saving"
                @click="saveVideo"
              >
                <span class="material-icons">{{ saving ? 'downloading' : 'download' }}</span>
                {{ saving ? $t('flyover_share.preparing') : $t('flyover_share.save_video') }}
              </button>
              <button class="flyover-share__pill" @click="copyLink">
                <span class="material-icons">link</span> {{ $t('flyover_share.copy_link') }}
              </button>
            </div>
            <div class="flyover-share__grid">
              <button v-for="t in shareTargets" :key="t.key" class="flyover-share__target" @click="runShareTarget(t)">
                <span class="flyover-share__target-icon">
                  <svg class="flyover-share__target-svg" viewBox="0 0 24 24" aria-hidden="true"><path :d="t.path" /></svg>
                </span>
                <small>{{ t.label }}</small>
              </button>
            </div>
          </div>
        </div>

        <!-- STEP: failed -->
        <div v-else-if="step === 'failed'" class="flyover-share__body">
          <span class="material-icons flyover-share__error-icon">error_outline</span>
          <h2 class="flyover-share__title">{{ $t('flyover_share.something_went_wrong') }}</h2>
          <p class="flyover-share__subtitle">{{ errorMessage || $t('flyover_share.could_not_create_video') }}</p>
          <div class="flyover-share__actions">
            <button class="flyover-share__btn flyover-share__btn--primary" @click="startGeneration">{{ $t('flyover_share.try_again') }}</button>
            <button class="flyover-share__btn flyover-share__btn--ghost" @click="hide">{{ $t('shared.close') }}</button>
          </div>
        </div>

        <!-- toast -->
        <div v-if="toast" class="flyover-share__toast">{{ toast }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { Modal } from 'bootstrap';
import lottie, { type AnimationItem } from 'lottie-web';
import flyoverLoadingData from '../../images/lottie/flyover_loading.json';
import pinMarker from '../../images/yellow-markers.webp';
import proPlusBadge from '../../images/PRO-PLUS_BADGE.svg';
import proPlusLogo from '../../images/PRO-PLUS.svg';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import {
  DEFAULT_VIDEO_OPTIONS,
  videoOptionRowsFor,
  allowedOptions,
  collapsedOptionsLabel as collapsedLabelFor,
  loadStoredOptions,
  optionsSummary,
  removedCount,
  saveStoredOptions,
  toggleOption,
  type FlyoverVideoOptionKey,
  type FlyoverVideoOptions,
} from './videoOptions';
import { trackVideoOptionChanged, trackVideoOptionsPaywall } from './videoOptionsAnalytics';
import graph_client from '../../utils/graphql/graph_client';
import { GenerateFlyoverVideo, FlyoverVideosForRecording } from '../../utils/graphql/recorded-trail-graph';
import { GenerateHikeTrailer, HikeTrailerVideos } from '../../utils/graphql/hikes-graph';
import { isHikeShare, buildGenerateRequest, videosFromPollData } from './trailerShare';
import { shareKind, trackFlyoverShareTarget, type ShareTargetKey } from './shareAnalytics';
import { shareCopy } from './shareCopy';
import { i18n } from '../../i18n';
import {
  pickReusableVideo,
  normalizeStyleUrl,
  isVerticalAspect,
  facebookShareUrl,
  whatsappShareUrl,
  whatsappShareText,
  emailShareUrl,
  emailShareBody,
  nativeSharePayload,
  primaryShareLink,
  videoFileName,
  ASPECT_VERTICAL,
  ASPECT_HORIZONTAL,
  type FlyoverVideoLite,
} from './shareActions';

interface ShareConfig {
  recordingId: string;
  // When set, the wizard shares a HIKE's Trail Trailer instead of a recording flyover: generation
  // goes through GenerateHikeTrailer and polling through hikeTrailerVideos. The steps, formats and
  // share targets are identical; only the copy differs (see shareCopy.ts).
  hikeId?: string;
  shareUrl: string;
  title: string;
  distance?: string;
  elevation?: string;
  duration?: string;
  thumbnailUrl?: string;
  // When provided, the wizard opens straight on the "ready" step for this already-rendered video
  // (used by the My-flyovers list's Share action) instead of querying/reusing by recording.
  video?: { url: string; aspect?: string | null; id?: string | null };
  // Style the video should render with. Omitted → the backend's default satellite style. Threaded in
  // by whoever opened the wizard (e.g. the flyover preview's current map style) so each style renders
  // and reuses as its own cached video.
  mapStyleUrl?: string;
  // Analytics only (see shareAnalytics.ts). `source` names the entry point that opened the wizard;
  // `kind` is only set where the wizard opens on an already-rendered video and so carries no subject
  // id (the My-flyovers list) — everywhere else the recordingId/hikeId decide.
  source?: string;
  kind?: 'flyover' | 'trailer';
}

const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

type Step = 'checking' | 'prompt' | 'chooseFormat' | 'generating' | 'ready' | 'failed';

const modalEl = ref<HTMLElement | null>(null);
let modal: Modal | null = null;

const lottieContainer = ref<HTMLElement | null>(null);
let lottieAnim: AnimationItem | null = null;

const step = ref<Step>('prompt');
const format = ref<'vertical' | 'horizontal'>('vertical');

// --- PRO+ video options ---
// `isProMember` starts false so the locked rows are what renders before the entitlement lookup
// resolves. That is the safe direction: showing a toggle and then replacing it with a padlock reads
// as the feature being taken away, while a padlock that becomes a toggle reads as it unlocking.
const isProMember = ref(false);
// Pre-filled from the last export on this browser — per-device, because it is a per-export choice
// rather than a profile setting. Filtered through the entitlement below so a set left over from a
// subscribed session cannot survive into a free one.
const videoOptions = ref<FlyoverVideoOptions>({ ...DEFAULT_VIDEO_OPTIONS });
// Collapsed for a member (one line, demanding nothing); always open for a free account, where the
// rows ARE the offer and there is nothing to collapse them to.
const optionsExpanded = ref(false);
const paywallOpen = ref(false);
// Four rows on a recording flyover, three on a hike trailer (which has no photos to hide).
const isTrailerShare = computed(() => isHikeShare(config));
const videoOptionRows = computed(() => videoOptionRowsFor(isTrailerShare.value));
// The HiiKER mountain mark, inline so it can take currentColor — the design shows it filled white
// inside the row's grey circle, and every bundled copy of the logo is a fixed-colour raster.
const hiikerMarkPath =
  'M 46.459 40.758 C 45.412 41.766 36.64 33.911 23.844 33.686 C 10.293 33.445 1.137 41.98 0.045 40.758 '
  + 'C -1.048 39.537 18.285 7.359 19.09 7.362 C 19.895 7.365 25.222 17.567 25.839 17.479 '
  + 'C 26.456 17.39 28.883 12.113 29.875 11.885 C 30.866 11.657 47.505 39.751 46.459 40.758 Z '
  + 'M 29.887 4.62 C 28.561 4.62 27.486 5.685 27.486 7 C 27.486 8.315 28.561 9.38 29.887 9.38 '
  + 'C 31.213 9.38 32.288 8.315 32.288 7 C 32.288 5.685 31.213 4.62 29.887 4.62 Z '
  + 'M 19.066 0 C 17.74 0 16.665 1.066 16.665 2.38 C 16.665 3.695 17.74 4.761 19.066 4.761 '
  + 'C 20.392 4.761 21.467 3.695 21.467 2.38 C 21.467 1.066 20.392 0 19.066 0 Z '
  + 'M 33.411 20.751 C 33.32 20.37 30.464 14.838 29.891 14.792 C 29.318 14.746 27.59 17.977 27.64 18.556 '
  + 'C 27.69 19.135 28.4 20.97 28.771 20.92 C 29.143 20.87 29.756 19.104 30.11 19.135 '
  + 'C 30.464 19.165 32.094 22.423 32.6 22.367 C 33.106 22.312 33.502 21.132 33.411 20.751 Z '
  + 'M 22.27 15.765 C 22.273 14.97 19.499 10.118 19.094 10.064 C 18.69 10.01 15.8 14.889 15.866 15.781 '
  + 'C 15.933 16.674 16.506 17.265 16.88 17.297 C 17.254 17.33 17.906 15.49 18.373 15.492 '
  + 'C 18.839 15.494 20.292 18.454 20.688 18.351 C 21.085 18.247 22.267 16.561 22.27 15.765 Z';
const optionsSummaryText = computed(() => optionsSummary(videoOptions.value, isTrailerShare.value));
const collapsedOptionsLabel = computed(() => collapsedLabelFor(videoOptions.value, isTrailerShare.value));
const config = reactive<ShareConfig>({ recordingId: '', shareUrl: '', title: '' });
// Recording flyover or hike trailer — decides the copy, never the flow.
const kind = computed(() => shareKind(config));
const copy = computed(() => shareCopy(kind.value));
const videoUrl = ref<string | null>(null);
const videoIsVertical = ref(true);
const progress = ref(0);
// A small client-side ramp shown immediately when generating starts, so the ring isn't stuck at 0
// before the first poll returns. The real API progress takes over via displayProgress once it exceeds
// this floor.
const syntheticFloor = ref(0);
let floorTimer: number | null = null;
const displayProgress = computed(() => Math.round(Math.max(progress.value, syntheticFloor.value)));
const errorMessage = ref<string | null>(null);
const toast = ref('');
// Immediate feedback for "Save video": the download streams through a same-origin proxy, so there's a
// beat before the browser's download prompt appears. `saving` drives a busy pill so the click doesn't
// look unresponsive.
const saving = ref(false);
let savingTimer: number | null = null;

let currentVideoId: string | null = null;
let pollTimer: number | null = null;

const tipIndex = ref(0);
let tipTimer: number | null = null;
const tips = [
  { get t() { return t('flyover_share.tip_tag_us'); }, get b() { return t('flyover_share.tip_tag_us_body'); } },
  { get t() { return t('flyover_share.tip_best_on_reels'); }, get b() { return t('flyover_share.tip_best_on_reels_body'); } },
  { get t() { return t('flyover_share.tip_stats_travel'); }, get b() { return t('flyover_share.tip_stats_travel_body'); } },
  { get t() { return t('flyover_share.tip_almost_there'); }, get b() { return copy.value.almostThereBody; } },
];

const ringStyle = computed(() => ({
  background: `conic-gradient(var(--yellow, #ffd400) ${displayProgress.value * 3.6}deg, rgba(255,255,255,0.15) 0deg)`,
}));

function startFloorRamp() {
  syntheticFloor.value = 0;
  if (floorTimer) window.clearInterval(floorTimer);
  // Ease up to ~12% over ~1.8s, then hold — the API drives the rest.
  floorTimer = window.setInterval(() => {
    if (syntheticFloor.value >= 12) {
      stopFloorRamp();
      return;
    }
    syntheticFloor.value += 1;
  }, 150);
}
function stopFloorRamp() {
  if (floorTimer) { window.clearInterval(floorTimer); floorTimer = null; }
}

// Share the rendered video's URL (the whole point of this flow), falling back to the hike/recording
// page only if the video URL isn't resolved yet. Targets that take free text carry the page link too
// (see shareActions.ts), so the clip arrives with a way back to the walk it shows.
const shareableLink = () => primaryShareLink(videoUrl.value, config.shareUrl);

const facebookAppShare = () => openUrl(facebookShareUrl(shareableLink()));
const whatsAppShare = () => openUrl(whatsappShareUrl(whatsappShareText(config.title, videoUrl.value, config.shareUrl)));
const emailShare = () => {
  window.location.href = emailShareUrl(config.title || copy.value.emailSubject, emailShareBody(videoUrl.value, config.shareUrl));
};

// Instagram / Stories / Reels / Messenger have no web share-a-link intent — use the browser's native
// share sheet where available (offers those apps on mobile web), else copy the link with a toast.
const nativeShare = async () => {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (nav.share) {
    try {
      await nav.share(nativeSharePayload(config.title, videoUrl.value, config.shareUrl));
      return;
    } catch {
      /* user cancelled or unsupported — fall through to copy */
    }
  }
  await writeClipboard(shareableLink());
  showToast(t('flyover_share.link_copied_open_app'));
};

// Brand glyphs (single path, 24x24) rendered as yellow silhouettes on a dark square, matching the
// app's share sheet. Reel reuses the Facebook mark and Stories the Instagram mark, as on the apps.
const ICON_PATHS = {
  facebook: 'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
  instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  whatsapp: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z',
  messenger: 'M.001 11.639C.001 4.949 5.246 0 12 0s11.999 4.95 11.999 11.639c0 6.689-5.245 11.638-11.999 11.638-1.214 0-2.377-.161-3.47-.46a.96.96 0 00-.64.033l-2.379 1.05a.96.96 0 01-1.348-.849l-.065-2.132a.96.96 0 00-.322-.68C1.809 18.216.001 15.194.001 11.639zm8.32-2.19l-3.525 5.591c-.338.537.322 1.14.827.76l3.79-2.87a.72.72 0 01.868-.002l2.807 2.104a1.8 1.8 0 002.605-.48l3.526-5.591c.338-.537-.322-1.14-.827-.76l-3.79 2.87a.72.72 0 01-.868.003l-2.808-2.105a1.8 1.8 0 00-2.605.48z',
  email: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  more: 'M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
};

interface ShareTargetTile {
  key: ShareTargetKey;
  label: string;
  path: string;
  action: () => void | Promise<void>;
}

const shareTargets = computed<ShareTargetTile[]>(() => [
  { key: 'facebook', label: 'Facebook', path: ICON_PATHS.facebook, action: facebookAppShare },
  { key: 'facebookReel', label: t('flyover_share.target_reel'), path: ICON_PATHS.facebook, action: facebookAppShare },
  { key: 'instagram', label: 'Instagram', path: ICON_PATHS.instagram, action: nativeShare },
  { key: 'instagramStories', label: t('flyover_share.target_stories'), path: ICON_PATHS.instagram, action: nativeShare },
  { key: 'messenger', label: 'Messenger', path: ICON_PATHS.messenger, action: nativeShare },
  { key: 'whatsapp', label: 'WhatsApp', path: ICON_PATHS.whatsapp, action: whatsAppShare },
  { key: 'email', label: t('shared.email'), path: ICON_PATHS.email, action: emailShare },
  { key: 'more', label: t('shared.more'), path: ICON_PATHS.more, action: nativeShare },
]);

/**
 * Every share tile goes through here so the analytics event and the action can't drift apart — a tile
 * added to `shareTargets` is tracked by construction. Tracking first (fire-and-forget) means a target
 * that navigates away — Email sets window.location, Facebook opens a new window — is still recorded.
 */
function runShareTarget(tile: ShareTargetTile) {
  trackShareTarget(tile.key);
  void tile.action();
}

/** The two pills above the grid ("Save video", "Copy link") are share targets too. */
function trackShareTarget(target: ShareTargetKey) {
  trackFlyoverShareTarget(config, target, videoIsVertical.value);
}

function openUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function writeClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}

let toastTimer: number | null = null;
function showToast(message: string) {
  toast.value = message;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toast.value = ''; }, 2500);
}

async function copyLink() {
  trackShareTarget('link');
  await writeClipboard(shareableLink());
  showToast(t('flyover_share.link_copied'));
}

function stopSaving() {
  if (savingTimer) { window.clearTimeout(savingTimer); savingTimer = null; }
  saving.value = false;
}

function saveVideo() {
  if (saving.value) return;
  trackShareTarget('saveVideo');
  // Show feedback synchronously (still inside the click gesture so the download isn't blocked) — the
  // proxy fetches the R2 MP4 server-side before the browser's prompt appears, and without this the
  // button looked dead. Cleared shortly after the download has been kicked off.
  saving.value = true;
  showToast(t('flyover_share.preparing_your_video'));

  // Download via the same-origin proxy, which streams the R2 MP4 back with an attachment disposition.
  // A direct link to the public R2 object opens inline in a new tab, and a client-side fetch→blob
  // download is CORS-blocked on the public bucket — the proxy sidesteps both so it downloads in place.
  if (currentVideoId) {
    const a = document.createElement('a');
    a.href = `/flyover_videos/${currentVideoId}/download`;
    a.download = videoFileName(config.title);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else if (videoUrl.value) {
    // No id resolved (shouldn't happen once ready) — fall back to opening the raw file.
    openUrl(videoUrl.value);
  }

  if (savingTimer) window.clearTimeout(savingTimer);
  savingTimer = window.setTimeout(() => {
    saving.value = false;
    savingTimer = null;
    showToast(t('flyover_share.download_started'));
  }, 1200);
}

/**
 * Resolve the entitlement and settle the option state around it. Runs when the wizard opens, not
 * when the format step is reached, so the rows are already correct the moment they render.
 *
 * `allowedOptions` is applied to the stored set as well: a member who lets their subscription lapse
 * would otherwise come back to the toggles they last used, silently have them dropped by the server,
 * and be handed a branded video they did not ask for.
 */
async function resolveVideoOptions() {
  const stored = loadStoredOptions();
  let isPro = false;
  try {
    isPro = await RevenueCatWrapper.isProOrProPlus();
  } catch {
    // Treat an entitlement lookup failure as "not entitled": the server would downgrade the render
    // anyway, so showing the toggles here could only promise something the render cannot deliver.
    isPro = false;
  }
  isProMember.value = isPro;
  videoOptions.value = allowedOptions(stored, isPro);
  // A free account never gets a collapsed row — the rows are the pitch. A member opens collapsed
  // unless they already have something set, in which case hiding it would hide their own choice.
  optionsExpanded.value = !isPro || removedCount(videoOptions.value, isTrailerShare.value) > 0;
}

function expandOptions() {
  optionsExpanded.value = true;
}

function onOptionRow(key: FlyoverVideoOptionKey) {
  if (!isProMember.value) {
    paywallOpen.value = true;
    trackVideoOptionsPaywall(config, key);
    return;
  }
  const next = toggleOption(videoOptions.value, key);
  videoOptions.value = next;
  saveStoredOptions(next);
  trackVideoOptionChanged(config, key, next[key], removedCount(next, isTrailerShare.value));
}

function upgradeToPro() {
  window.location.href = '/purchases';
}

async function startGeneration() {
  errorMessage.value = null;
  progress.value = 0;
  step.value = 'generating';
  startTips();
  startFloorRamp();
  try {
    // Filtered again at the point of sending rather than trusted from the UI state: the entitlement
    // can resolve (or a lapse can land) after the rows rendered, and the request is the last place
    // the client can agree with what the server is about to do.
    const options = allowedOptions(videoOptions.value, isProMember.value);
    const req = buildGenerateRequest(config, format.value, config.mapStyleUrl, options);
    const data: any = req.kind === 'hikeTrailer'
      ? await graph_client.request(GenerateHikeTrailer, { input: req.input })
      : await graph_client.request(GenerateFlyoverVideo, { input: req.input });
    const video = req.kind === 'hikeTrailer' ? data?.generateHikeTrailer : data?.generateFlyoverVideo;
    if (!video) throw new Error('No response');
    currentVideoId = video.id;
    if (video.status === 'READY' && video.url) {
      onReady(video);
    } else if (video.status === 'FAILED') {
      onFailed(video.errorMessage);
    } else {
      progress.value = Math.min(Math.max(video.progress ?? 0, 0), 100);
      startPolling();
    }
  } catch (e: any) {
    onFailed(e?.message);
  }
}

function startPolling() {
  stopPolling();
  pollTimer = window.setInterval(poll, 2000);
}

function stopPolling() {
  if (pollTimer) { window.clearInterval(pollTimer); pollTimer = null; }
}

async function poll() {
  try {
    const hike = isHikeShare(config);
    const data: any = hike
      ? await graph_client.request(HikeTrailerVideos, { hikeId: config.hikeId })
      : await graph_client.request(FlyoverVideosForRecording, { id: config.recordingId });
    const videos: any[] = videosFromPollData(data, hike ? 'hikeTrailer' : 'recordingFlyover');
    const video = videos.find((v) => v.id === currentVideoId);
    if (!video) return;
    progress.value = Math.min(Math.max(video.progress ?? 0, 0), 100);
    if (video.status === 'READY' && video.url) onReady(video);
    else if (video.status === 'FAILED') onFailed(video.errorMessage);
  } catch {
    /* transient poll error — keep polling */
  }
}

function onReady(video: { url: string; aspect?: string | null }) {
  stopPolling();
  stopTips();
  stopFloorRamp();
  videoUrl.value = video.url;
  videoIsVertical.value = isVerticalAspect(video.aspect);
  progress.value = 100;
  step.value = 'ready';
}

function onFailed(message?: string | null) {
  stopPolling();
  stopTips();
  stopFloorRamp();
  errorMessage.value = message ?? null;
  step.value = 'failed';
}

function startTips() {
  stopTips();
  tipIndex.value = 0;
  tipTimer = window.setInterval(() => { tipIndex.value = (tipIndex.value + 1) % tips.length; }, 3500);
}
function stopTips() {
  if (tipTimer) { window.clearInterval(tipTimer); tipTimer = null; }
}

function reset() {
  // Object.assign(config, cfg) never REMOVES keys, so a hike share followed by a recording share
  // would keep routing to the trailer mutation without this. The analytics fields need the same
  // clearing or a My-flyovers trailer share would file the NEXT share under kind=trailer too.
  config.hikeId = undefined;
  config.source = undefined;
  config.kind = undefined;
  step.value = 'prompt';
  format.value = 'vertical';
  videoUrl.value = null;
  videoIsVertical.value = true;
  progress.value = 0;
  syntheticFloor.value = 0;
  errorMessage.value = null;
  currentVideoId = null;
  paywallOpen.value = false;
  optionsExpanded.value = false;
  stopPolling();
  stopTips();
  stopFloorRamp();
  stopSaving();
}

async function showFlyover(cfg: ShareConfig) {
  reset();
  Object.assign(config, cfg);
  // Open immediately on a neutral "checking" spinner (responsive to the click) while we look for a
  // reusable render — this avoids flashing the prompt/format step before flipping to the share page.
  step.value = 'checking';
  modal?.show();
  // Awaited here rather than on the format step: the reuse pre-check below skips that step entirely,
  // and it needs to know which render the member is actually asking for.
  await resolveVideoOptions();
  // Opened for a specific, already-rendered video (My-flyovers list): jump straight to the ready
  // step with that video — no recording lookup needed.
  if (cfg.video?.url) {
    currentVideoId = cfg.video.id ?? null;
    onReady({ url: cfg.video.url, aspect: cfg.video.aspect });
    return;
  }
  // Hike trailers are cached per unit system as well as per style, and this client-side pre-check
  // only matches on style — it could hand a metric user someone's miles render. Let the MUTATION do
  // the reuse instead: it dedupes on the full key and returns the READY row instantly, so the only
  // cost is seeing the prompt/format step first.
  if (isHikeShare(config)) {
    step.value = 'prompt';
    return;
  }
  // Reuse a still-valid render for THIS style: jump straight to the share page. Only a READY video
  // rendered with the same map style qualifies (each style is its own cached render) — a different
  // style falls through to the generate flow. The backend deletes videos on a route edit, so no
  // freshness check is needed.
  try {
    const data: any = await graph_client.request(FlyoverVideosForRecording, { id: cfg.recordingId });
    const reusable = pickReusableVideo(
      (data?.recording?.flyoverVideos ?? []) as FlyoverVideoLite[],
      cfg.mapStyleUrl,
      videoOptions.value,
    );
    if (reusable && reusable.url) {
      currentVideoId = reusable.id ?? null;
      onReady({ url: reusable.url, aspect: reusable.aspect });
      return;
    }
  } catch {
    /* fall through to the full flow */
  }
  step.value = 'prompt';
}

function hide() {
  modal?.hide();
}

// Play the same loader animation as the apps (bundled lottie-web + the flyover_loading JSON) only
// while the generating step is on screen; tear it down otherwise.
watch(step, async (s) => {
  if (s === 'generating') {
    await nextTick();
    if (lottieContainer.value && !lottieAnim) {
      lottieAnim = lottie.loadAnimation({
        container: lottieContainer.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: flyoverLoadingData,
      });
    }
  } else if (lottieAnim) {
    lottieAnim.destroy();
    lottieAnim = null;
  }
});

onMounted(() => {
  if (!modalEl.value) return;
  modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: true });
  modalEl.value.addEventListener('hidden.bs.modal', () => {
    stopPolling();
    stopTips();
    stopFloorRamp();
  });
});

onBeforeUnmount(() => {
  stopPolling();
  stopTips();
  stopFloorRamp();
  stopSaving();
  lottieAnim?.destroy();
  lottieAnim = null;
});

defineExpose({ showFlyover });
</script>

<style scoped>
/* Centred modal box (not fullscreen), phone-sized so the app design reads well on web. */
.flyover-share__dialog { max-width: 460px; }
.flyover-share__content {
  background: #0f171e;
  color: #fff;
  border-radius: 18px;
  max-height: 92vh;
  overflow-y: auto;
}
.flyover-share__topbar {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
}
.flyover-share__close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.flyover-share__body {
  min-height: 380px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4px 24px 28px;
  gap: 16px;
}
.flyover-share__pin-img { height: 58px; width: auto; object-fit: contain; }
.flyover-share__title { font-size: 22px; font-weight: 800; margin: 0; }
.flyover-share__subtitle { color: rgba(255, 255, 255, 0.6); margin: 0; }
.flyover-share__map {
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  overflow: hidden;
}
.flyover-share__map img { width: 100%; display: block; }
/* Three equal columns with the labels pinned to the bottom row.
   Content-sized cells put each label directly under its own value, so a value long enough to wrap
   (a hike's estimate, or a recording's "4h 21m 13.0s") pushed its label a line lower than the other
   two and the row read as misaligned. `flex: 1 1 0` makes the columns equal regardless of string
   length, and `margin-top: auto` on the label keeps all three labels on one line however tall the
   value above them turns out to be. */
.flyover-share__stats { display: flex; gap: 16px; width: 100%; max-width: 360px; }
.flyover-share__stat {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.flyover-share__stat span { font-size: 24px; font-weight: 900; line-height: 1.15; overflow-wrap: break-word; }
.flyover-share__stat label {
  margin-top: auto;
  padding-top: 6px;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--yellow, #ffd400);
}
.flyover-share__actions { display: flex; flex-direction: column; gap: 12px; width: 100%; max-width: 360px; margin-top: 20px; }
.flyover-share__btn {
  border: none;
  border-radius: 24px;
  padding: 14px;
  font-weight: 700;
  cursor: pointer;
}
.flyover-share__btn--primary { background: var(--yellow, #ffd400); color: #111; }
.flyover-share__btn--ghost { background: transparent; color: #fff; border: 1px solid rgba(255, 255, 255, 0.25); }

.flyover-share__formats { display: flex; gap: 16px; }
.flyover-share__format {
  background: rgba(255, 255, 255, 0.06);
  border: 2px solid transparent;
  border-radius: 16px;
  padding: 16px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  width: 150px;
}
.flyover-share__format.is-selected { border-color: var(--yellow, #ffd400); }

/* --- PRO+ video options -------------------------------------------------------------------- */
/* Colours from the design tokens: --color-dark-3 #22242a panel, --color-dark-4 #2d2f35 dividers and
   icon circles, --color-gray-3 #acb0bf secondary text, --color-pro-blue #2cc2e3 for everything PRO+
   (active toggle, lock glyph, upgrade CTA) so the paid affordance never reads as the yellow CTA. */
/* Left-aligned against .flyover-share__body's centred text: these are list rows, and a centred
   sub-label under a left-aligned label reads as a layout bug. */
.flyover-share__options { width: 100%; max-width: 360px; margin-top: 16px; text-align: left; }
.flyover-share__options-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #22242a;
  border: none;
  border-radius: 8px;
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.flyover-share__options-icon {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #2d2f35;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flyover-share__options-icon svg { display: block; color: #fff; }
.flyover-share__options-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.flyover-share__options-title { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 700; }
.flyover-share__options-text small { font-size: 10px; color: #acb0bf; }
.flyover-share__options-state { flex: none; font-size: 11px; font-weight: 600; color: #acb0bf; }
.flyover-share__pro-badge { height: 15px; width: auto; display: block; border-radius: 3px; }
/* Chevron drawn as a rotated border corner — no icon font needed for two lines. */
.flyover-share__chevron {
  flex: none;
  width: 8px;
  height: 8px;
  border-top: 1.6px solid #acb0bf;
  border-right: 1.6px solid #acb0bf;
  transform: rotate(45deg);
}
.flyover-share__options-head { display: flex; align-items: center; gap: 8px; padding: 0 2px 10px; }
.flyover-share__options-heading { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #fff; }
.flyover-share__options-collapse {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 11px;
  font-weight: 600;
  color: #acb0bf;
  cursor: pointer;
}
.flyover-share__options-panel { background: #22242a; border-radius: 8px; overflow: hidden; }
.flyover-share__option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: none;
  border: none;
  border-top: 1px solid #2d2f35;
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.flyover-share__option:first-child { border-top: none; }
.flyover-share__option-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.flyover-share__option-text span { font-size: 13px; font-weight: 600; }
.flyover-share__option-text small { font-size: 10px; color: #acb0bf; }
.flyover-share__toggle {
  flex: none;
  width: 44px;
  height: 26px;
  border-radius: 100px;
  background: #505259;
  padding: 3px;
  display: flex;
  justify-content: flex-start;
}
.flyover-share__toggle.is-on { background: #2cc2e3; justify-content: flex-end; }
.flyover-share__toggle-knob { width: 20px; height: 20px; border-radius: 50%; background: #fff; }
.flyover-share__option-lock {
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #2d2f35;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flyover-share__options-summary { margin: 12px 0 0; font-size: 10px; color: #acb0bf; }

/* Fixed, not absolute: .flyover-share__content scrolls, so an absolutely-positioned scrim would be
   sized to the scroll height and slide away with the content instead of covering the flow. */
.flyover-share__paywall {
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.flyover-share__sheet {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  background: #1b1b1b;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -6px 14px rgba(0, 0, 0, 0.25);
  padding: 20px 22px 34px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}
.flyover-share__grab { width: 46px; height: 4px; border-radius: 100px; background: #505259; align-self: center; }
.flyover-share__sheet-logo { height: 24px; width: auto; align-self: flex-start; display: block; }
.flyover-share__sheet h3 { margin: 0; font-size: 20px; font-weight: 700; color: #fff; }
.flyover-share__sheet p { margin: 0; font-size: 12px; line-height: 1.5; color: #acb0bf; }
.flyover-share__sheet-bullets { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px; }
.flyover-share__sheet-bullets li { display: flex; align-items: center; gap: 10px; font-size: 12px; color: #fff; }
.flyover-share__sheet-bullets li::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2cc2e3;
  flex: none;
}
.flyover-share__btn--pro { background: #2cc2e3; color: #17181d; }
.flyover-share__sheet-dismiss {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #acb0bf;
  text-align: center;
  cursor: pointer;
}
.flyover-share__format small { color: rgba(255, 255, 255, 0.6); }
/* Phone-frame mockups (vertical / horizontal) — same orientation cue as the apps. */
.flyover-share__phone {
  position: relative;
  border: 3px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  background: linear-gradient(135deg, #2b6cb0, #63b3ed);
}
.flyover-share__phone--vertical { width: 52px; height: 92px; }
.flyover-share__phone--horizontal { width: 92px; height: 52px; }
.flyover-share__notch {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 3px;
}
/* Notch sits on the "top" edge relative to the phone's orientation. */
.flyover-share__phone--vertical .flyover-share__notch { top: 4px; left: 50%; transform: translateX(-50%); width: 18px; height: 3px; }
.flyover-share__phone--horizontal .flyover-share__notch { left: 4px; top: 50%; transform: translateY(-50%); width: 3px; height: 18px; }

.flyover-share__ring {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flyover-share__ring-inner {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: #0f171e;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flyover-share__lottie { width: 96px; height: 96px; }
.flyover-share__tip {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  text-align: left;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 14px;
  max-width: 420px;
}
.flyover-share__tip .material-icons { color: var(--yellow, #ffd400); }
.flyover-share__tip p { margin: 2px 0 0; color: rgba(255, 255, 255, 0.6); font-size: 13px; }

.flyover-share__ready { display: flex; flex-direction: column; }
.flyover-share__ready-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 4px 24px 12px;
}
.flyover-share__video {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.flyover-share__video video { border-radius: 16px; background: #000; display: block; }
/* Cap the vertical clip so the title + video + both action rows + the 8-target grid fit within the
   modal without scrolling. Height wins over width for a 9:16 clip. */
.flyover-share__video.is-vertical video { max-height: 38vh; width: auto; aspect-ratio: 9 / 16; }
.flyover-share__video.is-horizontal video { width: 100%; max-width: 100%; aspect-ratio: 16 / 9; }

.flyover-share__sheet {
  background: #1a232b;
  border-radius: 20px 20px 0 0;
  padding: 20px 16px 28px;
}
.flyover-share__sheet-row { display: flex; gap: 12px; margin-bottom: 16px; }
.flyover-share__pill {
  flex: 1;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 24px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}
.flyover-share__pill .material-icons { color: var(--yellow, #ffd400); font-size: 20px; }
.flyover-share__pill:disabled { cursor: default; }
/* Busy "Preparing…" state: pulse so the click reads as acknowledged while the proxy warms up. */
.flyover-share__pill.is-busy { animation: flyover-share-pulse 1s ease-in-out infinite; }
@keyframes flyover-share-pulse { 0%, 100% { opacity: 0.65; } 50% { opacity: 1; } }
.flyover-share__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.flyover-share__target { background: none; border: none; color: #fff; display: flex; flex-direction: column; align-items: center; gap: 6px; cursor: pointer; }
/* Dark rounded square with a yellow brand silhouette, matching the app's share sheet. */
.flyover-share__target-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: #0f171e; }
.flyover-share__target-svg { width: 22px; height: 22px; fill: var(--yellow, #ffd400); }
.flyover-share__target small { font-size: 12px; }

.flyover-share__error-icon { font-size: 56px; color: var(--yellow, #ffd400); }

.flyover-share__toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  z-index: 2000;
}
</style>
